(function () {
  /* base */
})();

// #region Utilities
function readTimeVar(css, name, fallbackMs) {
  const raw = (css.getPropertyValue(name) || "").trim();
  if (!raw) return fallbackMs;
  // supports "15000ms", "15s", "0", "0s", "0ms"
  const n = parseFloat(raw);
  if (isNaN(n)) return fallbackMs;
  // endsWith("ms") → milliseconds, endsWith("s") → seconds, else assume ms
  if (/ms$/i.test(raw)) return n;
  if (/s$/i.test(raw)) return Math.round(n * 1000);
  return n; // bare number → assume ms
}

// Get current pixel height safely (even mid-transition)
function currentHeight(el) {
  if (!el) return 0;
  // If it's already constrained, use that; otherwise measure box height
  const mh = parseFloat(getComputedStyle(el).maxHeight || "0");
  if (!isNaN(mh) && mh > 0) return mh;
  return el.getBoundingClientRect().height;
}

function safeReplace(parent, nextEl, prevEl) {
  try {
    if (!parent || !nextEl || !prevEl) return;
    if (prevEl.parentNode !== parent) {
      // Relationship changed mid-animation: just append next, remove prev if it’s still around
      if (!nextEl.isConnected) parent.appendChild(nextEl);
      if (prevEl.isConnected && prevEl.parentNode) prevEl.remove();
      return;
    }
    parent.replaceChild(nextEl, prevEl);
  } catch (_) {
    // Element already removed/replaced — ignore
  }
}
// #endregion Utilities

// #region Animations
// Reveal-up animation observer with group support
window.initRevealUp = function initRevealUp() {
  const prefersReduced =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReduced) return;

  // Clean up prior observers (PJAX / reinit safety)
  if (window.__revealUpObservers) {
    window.__revealUpObservers.forEach((io) => {
      try {
        io.disconnect();
      } catch {}
    });
  }
  window.__revealUpObservers = [];

  const els = Array.from(document.querySelectorAll(".fx-reveal-up"));
  if (!els.length) return;

  const inViewport = (el) => {
    const r = el.getBoundingClientRect();
    return (
      r.top < window.innerHeight * 0.9 && r.bottom > window.innerHeight * 0.1
    );
  };

  // Build groups by data-reveal-group (optional)
  // Each group gets a single anchor (the trigger line's clip wrapper or itself).
  const groupDefs = new Map(); // id -> { members: [], triggerEl: Element }
  for (const el of els) {
    const gid = el.getAttribute("data-reveal-group");
    if (!gid) continue;
    const def = groupDefs.get(gid) || { members: [], triggerEl: null };
    def.members.push(el);
    if (!def.triggerEl || el.hasAttribute("data-reveal-trigger")) {
      def.triggerEl = el;
    }
    groupDefs.set(gid, def);
  }
  // If no explicit trigger, use first member encountered
  for (const [gid, def] of groupDefs) {
    if (!def.triggerEl) def.triggerEl = def.members[0];
  }

  // Map anchors to the elements they should reveal
  // 1) All group members attach to the group's trigger anchor.
  // 2) Ungrouped elements attach to their own clip wrapper (legacy behavior).
  const anchorMap = new Map(); // anchorEl -> Element[] to reveal
  const addToAnchor = (anchor, elList) => {
    const list = anchorMap.get(anchor) || [];
    list.push(...elList);
    anchorMap.set(anchor, list);
  };

  // Grouped
  for (const { members, triggerEl } of groupDefs.values()) {
    const anchor = triggerEl.closest(".fx-clip, .fx-clip-inline") || triggerEl;
    addToAnchor(anchor, members);
  }

  // Ungrouped
  for (const el of els) {
    if (el.hasAttribute("data-reveal-group")) continue; // already mapped
    const anchor = el.closest(".fx-clip, .fx-clip-inline") || el;
    addToAnchor(anchor, [el]);
  }

  // Observe anchors; reveal their mapped elements together
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting || inViewport(entry.target)) {
          const targets = anchorMap.get(entry.target) || [];
          targets.forEach((t) => t.classList.add("fx-in"));
          io.unobserve(entry.target);
        }
      });
    },
    { threshold: 0, rootMargin: "0px 0px -10% 0px" }
  );
  window.__revealUpObservers.push(io);

  // Prime the initial state: reveal immediately if already in view, else observe
  for (const [anchor, targets] of anchorMap) {
    if (inViewport(anchor)) {
      // Use a frame to ensure CSS has applied
      requestAnimationFrame(() =>
        targets.forEach((t) => t.classList.add("fx-in"))
      );
    } else {
      io.observe(anchor);
    }
  }

  // Fallback for pages with no scroll
  if (document.body.scrollHeight <= window.innerHeight) {
    requestAnimationFrame(() => {
      for (const targets of anchorMap.values()) {
        targets.forEach((t) => t.classList.add("fx-in"));
      }
    });
  }
};

// Run once on initial load
window.initRevealUp();

// Smooth anchors (prefer Lenis if available; no header auto scroll)
document.addEventListener("click", (e) => {
  const a = e.target.closest('a[href^="#"]');
  if (!a) return;
  const id = a.getAttribute("href").slice(1);
  if (!id) return;
  const el = document.getElementById(id);
  if (!el) return;
  e.preventDefault();
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: 0, immediate: false, lock: true });
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
});
// #endregion Animations

// #region Lenis
(() => {
  if (!window.Lenis) return; // CDN not loaded or blocked
  if (window.__lenis) return; // Guard
  // Respect reduced motion
  const prefersReduced =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReduced) return;

  const lenis = new window.Lenis({
    smoothWheel: true,
    smoothTouch: true,
    syncTouch: true,
  });
  function raf(t) {
    lenis.raf(t);
    requestAnimationFrame(raf);
  }
  requestAnimationFrame(raf);
  window.__lenis = lenis;
})();
// #endregion Lenis

// #region Page Transition
(() => {
  const EASE = "cubic-bezier(0.65, 0, 0.35, 1)";
  const DUR_A = 900; // Cover in
  const DUR_B = 300; // Icons up/down
  const DUR_C = 300; // Icons split + logo reveal
  const DUR_D = 900; // Cover out
  const MOVE_Y = 67;
  const MOVE_X = 144;

  // Ensure browser doesn't “restore” previous scroll on history nav
  try {
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
  } catch {}

  const prefersReduced = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (prefersReduced) return;

  const appSel = "#app";
  const app = document.querySelector(appSel);
  const overlay = document.querySelector(".pt-overlay");
  const dimmer = document.querySelector(".pt-dimmer");
  const cover = document.querySelector(".pt-cover");
  const icons = document.querySelectorAll(".pt-icon");
  const logo = document.querySelector(".pt-logo");
  // Cache the icons wrapper & pristine markup so we can reconstruct every run
  const iconsWrap = document.querySelector(".pt-icons");
  const ICONS_HTML = iconsWrap ? iconsWrap.innerHTML : "";

  if (!app || !overlay || !dimmer || !cover || !icons.length || !logo) return;

  let navToken = 0;
  let preloaded = new Map(); // Cache fetched pages during overlay
  let exitHintAnim = null;

  function rebuildIcons() {
    if (!iconsWrap || !ICONS_HTML) return;
    iconsWrap.innerHTML = ICONS_HTML; // Guarantees 4 stacked icons at origin
  }

  const resetIcons = () => {
    document.querySelectorAll(".pt-icon").forEach((i) => {
      i.getAnimations().forEach((a) => a.cancel());
      i.style.transform = "translate3d(0,0,0)";
      i.style.opacity = "1";
      // Force reflow to kill any WAAPI 'forwards' leftovers
      i.getBoundingClientRect();
      i.style.transform = "translate3d(0,0,0)";
      i.style.opacity = "1";
    });
    // Hide logo until (C)
    logo.getAnimations().forEach((a) => a.cancel());
    logo.style.opacity = "0";
    logo.style.transform = "translateY(6px)";
    logo.style.visibility = "hidden";
    iconsWrap && (iconsWrap.style.visibility = "hidden");
    void iconsWrap?.offsetWidth;
  };

  // Helpers
  const guard = (t) => t === navToken;
  const sameOrigin = (url) => {
    try {
      const u = new URL(url, location.href);
      return u.origin === location.origin;
    } catch {
      return false;
    }
  };
  const normPath = (p) => {
    try {
      // Strip trailing slashes; keep root as "/"
      const q = p.replace(/\/+$/, "");
      return q === "" ? "/" : q;
    } catch {
      return p;
    }
  };
  const samePathAndSearch = (u) => {
    try {
      const url = u instanceof URL ? u : new URL(u, location.href);
      return (
        normPath(url.pathname) === normPath(location.pathname) &&
        (url.search || "") === (location.search || "")
      );
    } catch {
      return false;
    }
  };
  const allowIntercept = (a) => {
    if (!a) return false;
    const href = a.getAttribute("href") || "";
    if (href.startsWith("#")) return false;
    if (a.hasAttribute("download")) return false;
    if (a.target && a.target !== "_self") return false;
    if (!sameOrigin(href)) return false;
    if (/\/(cart|checkout|admin)\b/.test(href)) return false;
    if (window.Shopify && Shopify.designMode) return false; // Theme editor
    return true;
  };

  // Preload next page as soon as we click to hide fetch latency behind (A)(B)(C)
  async function preload(url) {
    if (preloaded.has(url)) return preloaded.get(url);
    const p = fetch(url, { credentials: "include" })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error("bad status"))))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const next = doc.querySelector(appSel);
        const title = doc.querySelector("title")?.textContent || document.title;
        if (!next || !next.childNodes || next.childNodes.length === 0) {
          throw new Error("empty app");
        }
        return { next, title };
      })
      .catch((err) => {
        // Do not cache failures; allow a later retry
        preloaded.delete(url);
        return null;
      });
    preloaded.set(url, p);
    return p;
  }

  function resetOverlay() {
    // clear inline animation effects
    overlay.hidden = true;
    overlay.style.pointerEvents = "none";
    dimmer.getAnimations().forEach((a) => a.cancel());
    cover.getAnimations().forEach((a) => a.cancel());
    logo.getAnimations().forEach((a) => a.cancel());
    // Cancel on the *current* icon elements (DOM may have been rebuilt)
    document
      .querySelectorAll(".pt-icon")
      .forEach((i) => i.getAnimations().forEach((a) => a.cancel()));
    dimmer.style.opacity = 0;
    dimmer.style.display = "";
    cover.style.transform = "translateY(100%)";
    rebuildIcons();
    resetIcons();
    // Make sure document can scroll again even if something errored
    document.body.classList.remove("pt-active");
    // Remove any leftover transform/opacity from the old page exit hint
    try {
      exitHintAnim?.cancel?.();
    } catch {}
    app.classList.remove("pt-exiting");
    app.style.removeProperty("transform");
    app.style.removeProperty("opacity");
  }

  // Timeline A: Exiting page hint + dimmer fade + cover slides up
  async function frameA(token) {
    // hard-close any UI that could animate on its own
    try {
      window.__closeMenuDrawer?.(true);
    } catch {
      // no-op
    }
    try {
      window.__closeTechDrawer?.(true);
    } catch {
      // no-op
    }
    rebuildIcons(); // brand-new DOM each transition
    resetIcons(); // zero transforms/opacities
    document.body.classList.add("pt-active");
    overlay.hidden = false;
    overlay.style.pointerEvents = "auto";

    // Pause Lenis so the old page can't scroll under overlay
    try {
      window.__lenis?.stop();
    } catch {
      // no-op
    }

    // Prep initial styles and force a frame.
    cover.style.transform = "translateY(100%)";
    dimmer.style.display = ""; // Make sure it can render
    dimmer.style.opacity = "0"; // Start at 0, fade to 1
    app.style.transform = "translateY(0px)";
    app.style.opacity = "1";
    await new Promise(requestAnimationFrame);

    // (A) start the COVER one frame earlier…
    const coverIn = cover.animate(
      [{ transform: "translateY(100%)" }, { transform: "translateY(0%)" }],
      { duration: DUR_A, easing: EASE, fill: "forwards" }
    );

    // …then on the very next frame, start exit-hint + dimmer.
    await new Promise(requestAnimationFrame);

    // Exiting page slides up exactly 152px and fades slightly (300ms)
    exitHintAnim = app.animate(
      [
        { transform: "translateY(0px)", opacity: 1 },
        { transform: "translateY(-152px)", opacity: 0.92 },
      ],
      { duration: 900, easing: EASE, fill: "forwards" }
    );
    app.classList.add("pt-exiting");

    // Dimmer fade (fire-and-forget; we don't need the Animation handle)
    dimmer.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 300,
      easing: EASE,
      fill: "forwards",
    });

    if (!guard(token)) return;

    await coverIn.finished.catch(() => {});

    // Remove the 30% dimmer immediately — cover is now fully up
    dimmer.getAnimations().forEach((a) => a.cancel());
    dimmer.style.opacity = "0";
    // IMPORTANT: Clear the old page transform before we swap new HTML
    try {
      exitHintAnim.cancel();
    } catch {
      // no-op
    }
    // Cover fully on — drop the 30% dimmer immediately (no lingering fade)
    dimmer.style.opacity = 0;
    dimmer.style.display = "none";

    app.classList.remove("pt-exiting");
    app.style.removeProperty("transform");
    app.style.removeProperty("opacity");
  }

  // Timeline B: Two icons up, two down
  async function frameB(token) {
    // Jump to top immediately (before icons animate)
    // Use behavior: 'instant' to bypass all smooth scrolling (including Lenis and CSS)
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    // Show icons only now (prevents any pre-frame paint)
    if (iconsWrap) iconsWrap.style.visibility = "visible";

    logo.style.opacity = "0";
    logo.style.transform = "translateY(6px)";
    const tops = document.querySelectorAll(".pt-icon--top");
    const bots = document.querySelectorAll(".pt-icon--bot");
    const ups = [...tops].map((i) =>
      i
        .animate(
          [
            { transform: "translateY(0px)" },
            { transform: `translateY(-${MOVE_Y}px)` },
          ],
          { duration: DUR_B, easing: EASE, fill: "forwards" }
        )
        .finished.catch(() => {})
    );
    const downs = [...bots].map((i) =>
      i
        .animate(
          [
            { transform: "translateY(0px)" },
            { transform: `translateY(${MOVE_Y}px)` },
          ],
          { duration: DUR_B, easing: EASE, fill: "forwards" }
        )
        .finished.catch(() => {})
    );
    await Promise.all([...ups, ...downs]);
    if (!guard(token)) return;
  }

  // Timeline C: Split left/right + logo fade in
  async function frameC(token) {
    // Ensure the logo is allowed to paint right before its fade-in
    logo.style.visibility = "visible";
    logo.style.opacity = "0";
    logo.style.transform = "translateY(6px)";
    const lefties = [
      document.querySelector(".pt-icon--top.a"),
      document.querySelector(".pt-icon--bot.a"),
    ].filter(Boolean);
    const righties = [
      document.querySelector(".pt-icon--top.b"),
      document.querySelector(".pt-icon--bot.b"),
    ].filter(Boolean);

    const L = lefties.map((i) =>
      i
        .animate(
          [
            { transform: getCurrentXY(i) },
            { transform: `translate3d(-${MOVE_X}px, ${composeY(i)}, 0)` },
          ],
          { duration: DUR_C, easing: EASE, fill: "forwards" }
        )
        .finished.catch(() => {})
    );
    const R = righties.map((i) =>
      i
        .animate(
          [
            { transform: getCurrentXY(i) },
            { transform: `translate3d(${MOVE_X}px, ${composeY(i)}, 0)` },
          ],
          { duration: DUR_C, easing: EASE, fill: "forwards" }
        )
        .finished.catch(() => {})
    );

    const logoIn = logo
      .animate(
        [
          { opacity: 0, transform: "translateY(6px)" },
          { opacity: 1, transform: "translateY(0px)" },
        ],
        { duration: DUR_C, easing: EASE, fill: "forwards" }
      )
      .finished.catch(() => {});

    await Promise.all([...L, ...R, logoIn]);
    if (!guard(token)) return;

    // once logo is in, we can hide the icons again so nothing lingers
    if (iconsWrap) iconsWrap.style.visibility = "hidden";

    function composeY(el) {
      // Preserve prior Y (from frame B) while adding X
      const movedTop = el.classList.contains("pt-icon--top");
      return `${movedTop ? -MOVE_Y : MOVE_Y}px`;
    }

    function getCurrentXY(el) {
      // After frame B, top icons are at -MOVE_Y, bottom at +MOVE_Y
      const y = el.classList.contains("pt-icon--top") ? -MOVE_Y : MOVE_Y;
      return `translate3d(0px, ${y}px, 0)`;
    }
  }

  // Timeline D: Slide cover up to reveal new page
  async function frameD(token) {
    const coverOut = cover.animate(
      [{ transform: "translateY(0%)" }, { transform: "translateY(-101%)" }],
      { duration: DUR_D, easing: EASE, fill: "forwards" }
    );
    await coverOut.finished.catch(() => {});
    if (!guard(token)) return;
    if (iconsWrap) iconsWrap.style.visibility = "hidden";
    resetOverlay();
    // Ensure we're at top (prefer Lenis so internal state matches)
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true, lock: true });
      window.__lenis.start(); // Resume smooth scroll now that new page is visible
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }

  async function fetchAndSwap(url, token, pre) {
    let data = pre || (await preload(url));
    if (!guard(token)) return;
    if (
      !data ||
      !data.next ||
      !data.next.childNodes ||
      data.next.childNodes.length === 0
    ) {
      // Bail out to a hard navigation rather than leaving <main> empty
      location.href = url;
      return;
    }
    // Swap children only to preserve the #app node
    app.replaceChildren(...data.next.childNodes);
    document.title = data.title;
    document.dispatchEvent(
      new CustomEvent("pjax:after-swap", { detail: { url } })
    );
    reinit();
  }

  function reinit() {
    try {
      // Re-run section initializers if present:
      // Close drawers instantly so they can't animate under the overlay
      window.__closeMenuDrawer?.(true);
      window.__closeTechDrawer?.(true);
      // Re-initialize menu drawer event listeners
      if (typeof window.initMenuDrawer === "function") window.initMenuDrawer();
      const who = document.querySelector("[data-who]");
      const useCases = document.querySelector("[data-use-cases]");
      if (
        who &&
        typeof seedAccordionImages === "function" &&
        typeof initAccordionSection === "function"
      ) {
        seedAccordionImages(who);
        initAccordionSection(who);
      }
      if (
        useCases &&
        typeof seedAccordionImages === "function" &&
        typeof initAccordionSection === "function"
      ) {
        seedAccordionImages(useCases);
        initAccordionSection(useCases);
      }
      if (typeof initWhoCarousel === "function") initWhoCarousel();
      if (typeof initTech === "function") initTech();
      // Re-anchor footer wordmark
      window.dispatchEvent(new Event("resize"));
      // Re-run reveal-up on the freshly swapped DOM (with extra ticks)
      if (typeof window.initRevealUp === "function") {
        window.requestAnimationFrame(() => {
          window.initRevealUp();
          setTimeout(window.initRevealUp, 0);
        });
      }
    } catch {}
  }

  async function navigate(url, replace = false) {
    const token = ++navToken;
    try {
      resetOverlay(); // Ensure a pristine starting state
      try {
        window.__closeMenuDrawer?.(true);
        window.__closeTechDrawer?.(true);
      } catch {
        // no-op
      }
      try {
        window.__lenis?.stop();
      } catch {
        // no-op
      }
      await new Promise(requestAnimationFrame); // Give resets a frame to settle
      const pre = preload(url); // Start preloading immediately
      await frameA(token);
      if (!guard(token)) return;
      await frameB(token);
      if (!guard(token)) return;
      await frameC(token);
      if (!guard(token)) return;
      await fetchAndSwap(url, token, await pre);
      if (!guard(token)) return;
      if (!guard(token)) return;
      if (replace) history.replaceState({}, "", url);
      else history.pushState({}, "", url);
      await frameD(token);
    } catch (e) {
      // Fail-safe: Full navigation
      location.href = url;
    } finally {
      // Always make sure scroll is unlocked even on error
      document.body.classList.remove("pt-active");
      app.classList.remove("pt-exiting");
      app.style.removeProperty("transform");
      app.style.removeProperty("opacity");
    }
  }

  // Click interception
  document.addEventListener("click", (e) => {
    if (document.body.classList.contains("pt-active")) return; // Ignore while animating
    const a = e.target.closest("a[href]");
    if (!a || !allowIntercept(a)) return;
    const url = new URL(a.href, location.href);
    // If same path+query, don't transition. Let anchor hashes behave normally.
    if (samePathAndSearch(url)) {
      // If there is a hash, you can optionally smooth-scroll here.
      // Example (uncomment to smooth):
      // if (url.hash) {
      //   const el = document.getElementById(url.hash.slice(1));
      //   if (el) { e.preventDefault(); el.scrollIntoView({behavior:'smooth', block:'start'}); }
      // }
      return; // no transition, no preventDefault
    }
    e.preventDefault();
    navigate(url.href);
  });

  window.addEventListener("popstate", () => {
    // Avoid re-running transition when the state is effectively the same
    // (rare, but defensive if browsers emit popstate without a URL change)
    if (samePathAndSearch(location.href)) return;
    navigate(location.href, true);
  });

  // Defensive reset if something leaves overlay visible
  window.addEventListener("pageshow", resetOverlay);
})();
// #endregion Page Transition

// #region Drawer
window.initMenuDrawer = () => {
  const drawer = document.querySelector("[data-menu-drawer]");
  const overlay = document.querySelector("[data-menu-overlay]");
  const openBtn = document.querySelector("[data-menu-open]");
  const closeBtn = document.querySelector("[data-menu-close]");
  if (!drawer || !overlay || !openBtn) return;

  // Clean up existing listeners if they exist
  if (window.__menuDrawerCleanup) {
    window.__menuDrawerCleanup();
  }

  const onEsc = (e) => {
    if (e.key === "Escape") close();
  };

  const open = () => {
    drawer.classList.add("is-open");
    overlay.hidden = false;
    overlay.classList.add("is-open");
    openBtn.setAttribute("aria-expanded", "true");
    drawer.setAttribute("aria-hidden", "false");
    document.addEventListener("keydown", onEsc);
  };

  const close = () => {
    drawer.classList.remove("is-open");
    overlay.classList.remove("is-open");
    // Match CSS 400ms so we hide after fade
    const css = getComputedStyle(drawer);
    const hideMs = readTimeVar(css, "--drawer-timing", 400);
    setTimeout(() => {
      overlay.hidden = true;
    }, hideMs);
    openBtn.setAttribute("aria-expanded", "false");
    drawer.setAttribute("aria-hidden", "true");
    document.removeEventListener("keydown", onEsc);
  };

  // Expose a no-animation closer for page transitions
  window.__closeMenuDrawer = (instant = false) => {
    if (!drawer || !overlay) return;
    if (!instant) return close();
    // kill CSS transitions just for this tick
    const prev1 = drawer.style.transition;
    const prev2 = overlay.style.transition;
    drawer.style.transition = "none";
    overlay.style.transition = "none";
    drawer.classList.remove("is-open");
    overlay.classList.remove("is-open");
    overlay.hidden = true;
    openBtn.setAttribute("aria-expanded", "false");
    drawer.setAttribute("aria-hidden", "true");
    document.removeEventListener("keydown", onEsc);
    // flush, then restore inline transition style
    void drawer.offsetHeight;
    drawer.style.transition = prev1;
    overlay.style.transition = prev2;
  };

  // Close drawer on scroll (per spec)
  let ticking = false;
  const onScroll = () => {
    if (!drawer.classList.contains("is-open")) return;
    if (!ticking) {
      window.requestAnimationFrame(() => {
        close();
        ticking = false;
      });
      ticking = true;
    }
  };

  // Close drawer when a drawer link is clicked
  const onDrawerClick = (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (a) close();
  };

  // Attach event listeners
  openBtn.addEventListener("click", open);
  overlay.addEventListener("click", close);
  if (closeBtn) closeBtn.addEventListener("click", close);
  window.addEventListener("scroll", onScroll, { passive: true });
  drawer.addEventListener("click", onDrawerClick);

  // Store cleanup function for PJAX
  window.__menuDrawerCleanup = () => {
    openBtn.removeEventListener("click", open);
    overlay.removeEventListener("click", close);
    if (closeBtn) closeBtn.removeEventListener("click", close);
    window.removeEventListener("scroll", onScroll);
    drawer.removeEventListener("click", onDrawerClick);
    document.removeEventListener("keydown", onEsc);
  };
};

// Initialize on page load
window.initMenuDrawer();
// #endregion Drawer

// #region Header
window.initHeader = function initHeader() {
  // Clean up any previous binding so re-inits after PJAX don’t stack listeners
  if (window.__headerCleanup) {
    try {
      window.__headerCleanup();
    } catch {}
    window.__headerCleanup = null;
  }

  const header = document.querySelector("[data-site-header]");
  if (!header) return; // Nothing to bind (some templates may omit it)

  const prefersReduced =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Compute a fresh trigger each init (header height can differ per page)
  const headerH = Math.round(header.getBoundingClientRect().height || 72);
  const TRIGGER_OFFSET = Math.max(0, headerH + 8); // ~header height + small buffer

  const setState = (state) => {
    header.classList.remove("is-transparent", "is-solid", "is-hidden");
    header.classList.add(state);
  };

  let lastY = window.scrollY;
  let direction = "down";

  const onScrollCore = () => {
    const y = window.scrollY;
    direction = y > lastY ? "down" : "up";
    lastY = y;
    if (y <= TRIGGER_OFFSET) {
      setState("is-transparent");
      return;
    }
    if (direction === "down") setState("is-hidden");
    else setState("is-solid");
  };

  // Throttled wrapper (or direct if reduced motion)
  let ticking = false;
  const onScroll = () => {
    if (prefersReduced) return onScrollCore();
    if (!ticking) {
      requestAnimationFrame(() => {
        onScrollCore();
        ticking = false;
      });
      ticking = true;
    }
  };

  // Bind
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });

  // Initial paint
  onScrollCore();

  // Expose cleanup so we can reinit after PJAX swaps safely
  window.__headerCleanup = () => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", onScroll);
  };
};

// First run
document.addEventListener("DOMContentLoaded", () => {
  if (typeof window.initHeader === "function") window.initHeader();
});

// Re-run after each PJAX swap
document.addEventListener("pjax:after-swap", () => {
  if (typeof window.initHeader === "function") window.initHeader();
});
// #endregion Header

// #region Contact Form
window.initContactForm = function initContactForm() {
  const form = document.getElementById("ContactForm");
  if (!form || form.dataset.bound === "1") return;
  form.dataset.bound = "1";

  const btn = form.querySelector(".contact-form__submit");
  if (!btn) return;

  // If server rendered success (after redirect), keep "done" state.
  const wasSuccess = !!form.querySelector("[data-contact-success]");
  if (wasSuccess) {
    btn.dataset.state = "done";
    btn.removeAttribute("aria-busy");
    return;
  }

  // If there were Liquid-rendered errors, ensure we're not stuck in loading.
  const hadErrors = !!form.querySelector(".contact-form__errors");
  if (hadErrors) {
    btn.dataset.state = "idle";
    btn.removeAttribute("aria-busy");
  }

  // Animate on submit, then let the browser POST to /contact
  form.addEventListener("submit", () => {
    if (btn.dataset.state === "loading") return;
    btn.dataset.state = "loading";
    btn.setAttribute("aria-busy", "true");
    // no preventDefault — allow the actual submit to happen
  });
};

// First run
document.addEventListener("DOMContentLoaded", () => {
  if (typeof window.initContactForm === "function") window.initContactForm();
});
// Re-run after PJAX swaps
document.addEventListener("pjax:after-swap", () => {
  if (typeof window.initContactForm === "function") window.initContactForm();
});
// #endregion Contact Form

// #region Comparison
(function () {
  // Clear previous binds after PJAX re-runs
  if (!window.__comparisonCleanups) window.__comparisonCleanups = [];
  window.__comparisonCleanups.forEach((fn) => {
    try {
      fn();
    } catch {}
  });
  window.__comparisonCleanups = [];

  function attachComparison(section) {
    const headers = section.querySelector("[data-comp-headers]");
    const cells = section.querySelector("[data-comp-cells]");
    const leftCol = section.querySelector("[data-comp-left]");
    const grid = section.querySelector(".comparison__grid");
    if (!headers || !cells || !leftCol || !grid) return;

    // Compute "stuck" state: headers are sticky at top:0 on desktop
    // Stuck when header's top has reached (<=0) AND cells list hasn't ended under it.
    function recomputeStickState() {
      const hRect = headers.getBoundingClientRect();
      const cRect = cells.getBoundingClientRect();
      const headerH = Math.max(1, headers.offsetHeight || 1);
      const stuckNow = hRect.top <= 0.5 && cRect.bottom > headerH;
      section.classList.toggle("is-stuck", stuckNow);
    }

    // Initial
    recomputeStickState();

    // Hook Lenis if present; fall back to native scroll
    let offScroll = null;
    if (window.__lenis && typeof window.__lenis.on === "function") {
      const handler = () => recomputeStickState();
      window.__lenis.on("scroll", handler);
      offScroll = () => {
        if (typeof window.__lenis.off === "function")
          window.__lenis.off("scroll", handler);
      };
    } else {
      const onScroll = () => recomputeStickState();
      window.addEventListener("scroll", onScroll, { passive: true });
      offScroll = () => window.removeEventListener("scroll", onScroll);
    }

    const onResize = () => recomputeStickState();
    window.addEventListener("resize", onResize, { passive: true });

    // If table content changes size (images/fonts), keep it accurate
    const ro = new ResizeObserver(() => recomputeStickState());
    try {
      ro.observe(headers);
      ro.observe(cells);
    } catch {}

    // Observe when bottom of grid comes into view
    // We check if the bottom edge of the grid is visible in viewport
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const gridRect = grid.getBoundingClientRect();
            const bottomInView = gridRect.bottom <= window.innerHeight;
            section.classList.toggle("at-bottom", bottomInView);
          } else {
            section.classList.remove("at-bottom");
          }
        });
      },
      { threshold: [0, 0.1, 0.5, 1], rootMargin: "0px" }
    );
    io.observe(grid);

    // Also check on scroll for more precision
    const checkBottom = () => {
      const gridRect = grid.getBoundingClientRect();
      const bottomInView = gridRect.bottom <= window.innerHeight;
      section.classList.toggle("at-bottom", bottomInView);
    };

    // Initial check
    checkBottom();

    let offBottomCheck = null;
    if (window.__lenis && typeof window.__lenis.on === "function") {
      window.__lenis.on("scroll", checkBottom);
      offBottomCheck = () => {
        if (typeof window.__lenis.off === "function")
          window.__lenis.off("scroll", checkBottom);
      };
    } else {
      window.addEventListener("scroll", checkBottom, { passive: true });
      offBottomCheck = () => window.removeEventListener("scroll", checkBottom);
    }

    // Cleanup for PJAX
    return () => {
      offScroll && offScroll();
      offBottomCheck && offBottomCheck();
      window.removeEventListener("resize", onResize);
      try {
        ro.disconnect();
      } catch {}
      try {
        io.disconnect();
      } catch {}
      section.classList.remove("is-stuck");
      section.classList.remove("at-bottom");
    };
  }

  function initComparison() {
    document.querySelectorAll("[data-comparison]").forEach((section) => {
      const cleanup = attachComparison(section);
      if (typeof cleanup === "function")
        window.__comparisonCleanups.push(cleanup);
    });
  }

  document.addEventListener("DOMContentLoaded", initComparison);
  document.addEventListener("pjax:after-swap", initComparison);
})();
// #endregion Comparison

// #region Features
(() => {
  const TARGET_THRESHOLD = 100; // Target px from trigger to allow active change on desktop

  // PJAX cleanup registry
  window.__featuresReinitCleanups ||= [];
  const runCleanups = () => {
    window.__featuresReinitCleanups.forEach((fn) => {
      try {
        fn();
      } catch {}
    });
    window.__featuresReinitCleanups = [];
  };

  const MQ = window.matchMedia("(min-width:1024px)");
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  function attach(section) {
    const scroller = section.querySelector(".features__scrollable");
    const list = section.querySelector(".features__scrollable__item-list");
    const items = Array.from(section.querySelectorAll(".features__item"));
    const images = Array.from(section.querySelectorAll(".features__image"));
    const imagesContainer = section.querySelector(".features__images");
    const indices = section.querySelector(".features__indices");
    const odoTrack = section.querySelector(".features__odometer__track");
    const odoDigits = Array.from(
      section.querySelectorAll(".features__odometer__digit")
    );
    const mobDigits = Array.from(
      section.querySelectorAll(".features__counter__digit")
    );
    const barTrack = section.querySelector(".features__scrollbar");
    const barInner = section.querySelector(".features__scrollbar__inner");

    if (!list || !items.length) return () => {};

    let active = 0;
    let rafId = 0;
    let running = false;
    const digitHandlers = new Map(); // Store event handlers for cleanup

    // Touch tracking for image swipe
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;

    // Prevent intermediate updates during programmatic scroll
    let isProgrammaticScroll = false;
    let programmaticScrollTimeout = null;

    const setActive = (idx) => {
      idx = clamp(idx, 0, items.length - 1);
      if (idx === active) return;
      active = idx;

      items.forEach((el, i) => el.classList.toggle("is-active", i === active));
      images.forEach((im, i) => im.classList.toggle("is-active", i === active));
      odoDigits.forEach((d, i) =>
        d.classList.toggle("is-active", i === active)
      );
      mobDigits.forEach((d, i) =>
        d.classList.toggle("is-active", i === active)
      );

      if (MQ.matches && odoTrack) {
        odoTrack.style.transform = `translateY(-${19 * active}px)`;
      }
    };

    // Helper to navigate to item programmatically
    const navigateToItem = (idx) => {
      if (!items[idx]) return;

      // Set flag to prevent intermediate updates
      isProgrammaticScroll = true;
      clearTimeout(programmaticScrollTimeout);

      // Immediately set target as active (no brackets during scroll)
      setActive(idx);

      // Scroll to item
      items[idx].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });

      // Clear flag after scroll completes (~500ms for smooth scroll)
      programmaticScrollTimeout = setTimeout(() => {
        isProgrammaticScroll = false;
      }, 600);
    };

    // Mobile
    const updateMobile = () => {
      if (!scroller) return;

      if (barTrack && barInner) {
        const maxX = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        const x = scroller.scrollLeft;
        const progress = maxX > 0 ? x / maxX : 0;
        const trackW = barTrack.clientWidth;
        const innerW = barInner.clientWidth || 74;
        const maxTx = Math.max(0, trackW - innerW);
        barInner.style.transform = `translateX(${progress * maxTx}px)`;
      }

      // Skip active item detection during programmatic scroll
      if (isProgrammaticScroll) return;

      // Active = nearest to scroller center
      const vr = scroller.getBoundingClientRect();
      const center = vr.left + vr.width / 2;
      let best = active,
        bestDist = Infinity;
      for (let i = 0; i < items.length; i++) {
        const r = items[i].getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const d = Math.abs(cx - center);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      }
      setActive(best);
    };

    const onMobileScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateMobile);
    };

    // Desktop
    const updateDesktop = () => {
      if (!indices) return;
      const triggerY = indices.getBoundingClientRect().top;

      // Find candidate nearest to trigger
      let best = active,
        bestScore = Infinity;
      for (let i = 0; i < items.length; i++) {
        const t = items[i].getBoundingClientRect().top;
        const s = Math.abs(t - triggerY);
        if (s < bestScore) {
          bestScore = s;
          best = i;
        }
      }

      // Only switch active if within threshold of target
      if (best !== active && bestScore <= TARGET_THRESHOLD) {
        setActive(best);
      }
    };

    // rAF loop
    const tick = () => {
      if (!running) return;
      if (MQ.matches) updateDesktop();
      else updateMobile();
      rafId = requestAnimationFrame(tick);
    };
    const start = () => {
      if (!running) {
        running = true;
        rafId = requestAnimationFrame(tick);
      }
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(rafId);
    };

    // Always run while in view; pause offscreen
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e && (e.isIntersecting || e.intersectionRatio > 0)) start();
        else stop();
      },
      { root: null, threshold: 0, rootMargin: "200px 0px 200px 0px" }
    );
    io.observe(section);

    // Event binds
    if (scroller)
      scroller.addEventListener("scroll", onMobileScroll, { passive: true });

    // Mobile: Make counter digits tappable to navigate
    if (!MQ.matches && scroller && mobDigits.length) {
      mobDigits.forEach((digit, idx) => {
        digit.style.cursor = "pointer";
        const onClick = () => {
          navigateToItem(idx);
        };
        const onTouchEnd = (e) => {
          e.preventDefault();
          onClick();
        };
        digit.addEventListener("click", onClick);
        digit.addEventListener("touchend", onTouchEnd);
        digitHandlers.set(digit, { onClick, onTouchEnd });
      });
    }

    // Mobile: Enable swipe on images to navigate features
    const imageSwipeHandlers = {};
    if (!MQ.matches && imagesContainer && scroller) {
      const onTouchStart = (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchStartTime = Date.now();
      };

      const onTouchMove = (e) => {
        // Allow natural scrolling, we'll detect swipe direction on touchend
      };

      const onTouchEnd = (e) => {
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        const touchDuration = Date.now() - touchStartTime;

        const deltaX = touchEndX - touchStartX;
        const deltaY = touchEndY - touchStartY;
        const absDeltaX = Math.abs(deltaX);
        const absDeltaY = Math.abs(deltaY);

        // Swipe threshold: horizontal movement > 50px, mostly horizontal, quick swipe < 300ms
        const isHorizontalSwipe = absDeltaX > 50 && absDeltaX > absDeltaY * 1.5 && touchDuration < 300;

        if (isHorizontalSwipe) {
          if (deltaX > 0) {
            // Swipe right -> previous item
            const prevIdx = Math.max(0, active - 1);
            navigateToItem(prevIdx);
          } else {
            // Swipe left -> next item
            const nextIdx = Math.min(items.length - 1, active + 1);
            navigateToItem(nextIdx);
          }
        }
      };

      imagesContainer.addEventListener("touchstart", onTouchStart, { passive: true });
      imagesContainer.addEventListener("touchmove", onTouchMove, { passive: true });
      imagesContainer.addEventListener("touchend", onTouchEnd, { passive: true });

      imageSwipeHandlers.onTouchStart = onTouchStart;
      imageSwipeHandlers.onTouchMove = onTouchMove;
      imageSwipeHandlers.onTouchEnd = onTouchEnd;
    }

    // Initial paint
    setActive(0);
    // Force a couple of immediate passes so sticky/layout settle when mounted
    requestAnimationFrame(() => {
      MQ.matches ? updateDesktop() : updateMobile();
      requestAnimationFrame(() =>
        MQ.matches ? updateDesktop() : updateMobile()
      );
    });

    // Cleanup for this section
    return () => {
      stop();
      clearTimeout(programmaticScrollTimeout);
      try {
        io.disconnect();
      } catch {}
      if (scroller) scroller.removeEventListener("scroll", onMobileScroll);
      // Clean up digit event listeners
      digitHandlers.forEach((handlers, digit) => {
        digit.removeEventListener("click", handlers.onClick);
        digit.removeEventListener("touchend", handlers.onTouchEnd);
        digit.style.cursor = "";
      });
      digitHandlers.clear();
      // Clean up image swipe handlers
      if (imagesContainer && imageSwipeHandlers.onTouchStart) {
        imagesContainer.removeEventListener("touchstart", imageSwipeHandlers.onTouchStart);
        imagesContainer.removeEventListener("touchmove", imageSwipeHandlers.onTouchMove);
        imagesContainer.removeEventListener("touchend", imageSwipeHandlers.onTouchEnd);
      }
    };
  }

  function boot() {
    document.querySelectorAll("[data-features]").forEach((section) => {
      const off = attach(section);
      if (typeof off === "function") window.__featuresReinitCleanups.push(off);
    });
  }

  // Hard re-init on resize or MQ change (fixes the “in view but dormant” case)
  let resizeTimer = 0;
  const hardReinit = () => {
    // Debounce a tad to avoid thrash
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      runCleanups();
      boot();
    }, 50);
  };

  // Wire up lifecycle
  document.addEventListener("DOMContentLoaded", boot);
  document.addEventListener("pjax:after-swap", () => {
    runCleanups();
    boot();
  });

  // Re-init on any resize + media query change
  window.addEventListener("resize", hardReinit, { passive: true });
  MQ.addEventListener?.("change", hardReinit);
})();
// #endregion Features

// #region Grid Hero
(() => {
  // Prevent double-binds across PJAX
  window.__gridHeroCleanups ||= [];
  window.__gridHeroCleanups.forEach((fn) => {
    try {
      fn();
    } catch {}
  });
  window.__gridHeroCleanups = [];

  function initGridHero(section) {
    const hWrap = section.querySelector(".grid-hero__horizontal-lines");
    const vWrap = section.querySelector(".grid-hero__vertical-lines");
    const hLines = hWrap ? Array.from(hWrap.children) : [];
    const vLines = vWrap ? Array.from(vWrap.children) : [];
    if (!hWrap || !vWrap || !hLines.length || !vLines.length) return;

    // Center-out stagger: set --d (delay) per line
    const hCenter = (hLines.length - 1) / 2; // 11 -> 5.5 (fine)
    const vCenter = (vLines.length - 1) / 2; // 15 -> 7.5
    const STEP = 0; // ms per “ring” from center; tweak to taste

    hLines.forEach((el, i) => {
      const dist = Math.abs(i - hCenter);
      el.style.setProperty("--d", `${Math.round(dist * STEP)}ms`);
    });
    vLines.forEach((el, i) => {
      const dist = Math.abs(i - vCenter);
      el.style.setProperty("--d", `${Math.round(dist * STEP)}ms`);
    });

    // Fire the entrance once the section is visible (usually the first component, but be safe)
    const loadIn = () => {
      hWrap.classList.add("loaded");
      vWrap.classList.add("loaded");
    };

    // If there's a page transition, wait until after PJAX swap has painted
    // Otherwise, use an IntersectionObserver so short pages don't miss it
    let io = null;
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => {
          const e = entries[0];
          if (e && (e.isIntersecting || e.intersectionRatio > 0)) {
            loadIn();
            io.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      io.observe(section);
    } else {
      // Fallback: next frame
      requestAnimationFrame(loadIn);
    }

    // Cleanup
    return () => {
      if (io)
        try {
          io.disconnect();
        } catch {}
    };
  }

  function boot() {
    document.querySelectorAll("#grid-hero.grid-hero").forEach((sec) => {
      const off = initGridHero(sec);
      if (typeof off === "function") window.__gridHeroCleanups.push(off);
    });
  }

  document.addEventListener("DOMContentLoaded", boot);
  document.addEventListener("pjax:after-swap", () => {
    // Give transition a tick to finish before animating
    requestAnimationFrame(boot);
  });
})();
// #endregion Grid Hero

// #region Technology
function initTech() {
  const section = document.querySelector("[data-tech]");
  if (!section) return;
  // Prevent double-binding if PJAX calls us again on the same DOM
  if (section.dataset.init === "1") return;
  section.dataset.init = "1";

  const hero = section.querySelector("[data-feature-hero]");
  const btns = Array.from(section.querySelectorAll("[data-feature-btn]"));
  const bankImgs = Array.from(
    section.querySelectorAll("[data-feature-bank] [data-feature-img]")
  );
  const card = section.querySelector("[data-feature-card]");
  const cardText = card?.querySelector("[data-feature-card-text]");

  let activeIdx = Math.max(
    0,
    btns.findIndex((b) => b.classList.contains("is-active"))
  );
  let hoverIdx = null;

  // Returns the <img> element for index i
  const bankElFor = (i) => bankImgs[i];

  const swapHeroFrom = (srcEl) => {
    if (!hero || !srcEl) return;

    // Read next sources from the bank image (not just src!)
    const nextSrc = srcEl.currentSrc || srcEl.getAttribute("src");
    const nextSrcset = srcEl.getAttribute("srcset");
    const nextSizes = srcEl.getAttribute("sizes");

    hero.style.opacity = "0.85";
    requestAnimationFrame(() => {
      // To be extra-safe in Safari, clear first then set
      if (nextSrcset) {
        hero.setAttribute("srcset", nextSrcset);
      } else {
        hero.removeAttribute("srcset");
      }
      if (nextSizes) {
        hero.setAttribute("sizes", nextSizes);
      } else {
        hero.removeAttribute("sizes");
      }
      hero.setAttribute("src", nextSrc);
      hero.width = srcEl.width;
      hero.height = srcEl.height;

      requestAnimationFrame(() => (hero.style.opacity = "1"));
    });
  };
  const swapHero = (i) => swapHeroFrom(bankElFor(i));
  const updateCard = (i) => {
    if (!card || !cardText) return;
    const text = btns[i]?.dataset.cardText;
    if (text) {
      cardText.textContent = text;
      card.hidden = false;
    } else {
      card.hidden = true;
    }
  };
  const setActive = (i) => {
    activeIdx = i;
    btns.forEach((b, j) => b.classList.toggle("is-active", j === i));
    if (hoverIdx == null) {
      swapHero(i);
      updateCard(i);
    }
  };
  btns.forEach((btn, i) => {
    btn.addEventListener("mouseenter", () => {
      hoverIdx = i;
      swapHero(i);
      updateCard(i);
    });
    btn.addEventListener("mouseleave", () => {
      hoverIdx = null;
      swapHero(activeIdx);
      updateCard(activeIdx);
    });
    btn.addEventListener("click", () => setActive(i));
  });
  if (activeIdx < 0) activeIdx = 0;
  swapHero(activeIdx);
  updateCard(activeIdx);

  const tracks = Array.from(section.querySelectorAll("[data-tech-track]"));
  const setupTrack = (track) => {
    const shell = track.closest(".tech__cards");
    const barTrack = shell?.querySelector("[data-tech-scrollbar]");
    const bar = barTrack?.querySelector("[data-tech-scrollbar-bar]");
    const prev = shell?.querySelector("[data-tech-prev]");
    const next = shell?.querySelector("[data-tech-next]");

    const updateBar = () => {
      if (!bar || !barTrack) return;
      const vis = track.getBoundingClientRect().width;
      const total = track.scrollWidth;
      const maxScroll = Math.max(0, total - vis);
      const trackW = barTrack.clientWidth;

      const width = maxScroll > 0 ? (vis / total) * trackW : trackW;
      bar.style.width = `${Math.max(24, width)}px`;

      const progress = maxScroll > 0 ? track.scrollLeft / maxScroll : 0;
      const maxX = trackW - bar.clientWidth;
      bar.style.transform = `translateX(${Math.max(0, Math.min(maxX, maxX * progress))}px)`;
    };

    const scrollByStep = (dir) => {
      const step = Math.round(track.getBoundingClientRect().width * 0.9);
      track.scrollBy({ left: dir * step, behavior: "smooth" });
      requestAnimationFrame(updateBar);
    };

    track.addEventListener("scroll", updateBar, { passive: true });
    window.addEventListener("resize", updateBar);
    prev?.addEventListener("click", () => scrollByStep(-1));
    next?.addEventListener("click", () => scrollByStep(1));

    // Ensure bar is correct once images report size
    const once = () => updateBar();
    track
      .querySelectorAll("img")
      .forEach((im) => im.addEventListener("load", once, { once: true }));
    updateBar();
  };
  tracks.forEach(setupTrack);

  const overlay = section.querySelector("[data-tech-overlay]");
  const drawer = section.querySelector("[data-tech-drawer]");
  const body = section.querySelector("[data-tech-drawer-body]");
  const closeBtn = section.querySelector("[data-tech-close]");
  if (!overlay || !drawer || !body) return;

  const openDrawer = () => {
    drawer.classList.add("is-open");
    overlay.hidden = false;
    overlay.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("is-tech-drawer-open");
    setTimeout(() => closeBtn?.focus(), 10);
  };
  const closeDrawer = () => {
    drawer.classList.remove("is-open");
    overlay.classList.remove("is-open");
    setTimeout(
      () => (overlay.hidden = true),
      parseInt(getComputedStyle(drawer).getPropertyValue("--drawer-timing")) ||
        400
    );
    drawer.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("is-tech-drawer-open");
  };
  // Expose a no-animation closer for page transitions
  window.__closeTechDrawer = (instant = false) => {
    if (!drawer || !overlay) return;
    if (!instant) return closeDrawer();
    const p1 = drawer.style.transition;
    const p2 = overlay.style.transition;
    drawer.style.transition = "none";
    overlay.style.transition = "none";
    drawer.classList.remove("is-open");
    overlay.classList.remove("is-open");
    overlay.hidden = true;
    drawer.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("is-tech-drawer-open");
    void drawer.offsetHeight;
    drawer.style.transition = p1;
    overlay.style.transition = p2;
  };
  overlay.addEventListener("click", closeDrawer);
  closeBtn?.addEventListener("click", closeDrawer);
  document.addEventListener(
    "keydown",
    (e) => e.key === "Escape" && closeDrawer()
  );

  function buildDrawerFromCards(cards, openIndex) {
    // Reset body and add one eyebrow
    body.innerHTML = "";
    const topEyebrow = document.createElement("span");
    topEyebrow.className = "tech__drawer-eyebrow";
    topEyebrow.textContent = "[TECHNOLOGY]";
    body.appendChild(topEyebrow);

    const acc = document.createElement("div");
    acc.className = "tech__drawer-acc";

    // We'll capture refs for the initially-open item so we can open it with no animation
    let firstItem = null;
    let firstPanel = null;
    let firstInner = null;
    let firstHead = null;

    cards.forEach((card, idx) => {
      const title =
        card.querySelector("[data-card-title]")?.textContent?.trim() ||
        `Item ${idx + 1}`;
      const tpl = card.querySelector("[data-drawer-content]");
      const imgSrc =
        card.querySelector("[data-card-img]")?.getAttribute("src") || "";

      const item = document.createElement("div");
      item.className = "tech__drawer-item";

      // Header
      const head = document.createElement("button");
      head.type = "button";
      head.className = "tech__drawer-head";
      head.innerHTML = `
      <span class="tech__drawer-index">${String(idx + 1).padStart(2, "0")}</span>
      <h4 class="tech__drawer-title--h">${title}</h4>
    `;
      item.appendChild(head);

      // Panel
      const panel = document.createElement("div");
      panel.className = "tech__drawer-panel";
      const inner = document.createElement("div");
      inner.className = "tech__drawer-panel-inner";

      // Accessibility
      const headId = `tech-head-${idx}`;
      head.id = headId;
      const panelId = `tech-panel-${idx}`;
      panel.id = panelId;
      head.setAttribute("aria-controls", panelId);
      head.setAttribute("aria-expanded", String(idx === openIndex));
      panel.setAttribute("role", "region");
      panel.setAttribute("aria-labelledby", headId);

      // Body copy (no eyebrow here)
      const copy = tpl?.querySelector(".tech__drawer-text")?.outerHTML || "";
      if (copy) {
        inner.insertAdjacentHTML("beforeend", copy);
      }

      // Image
      const imgWrap = document.createElement("div");
      imgWrap.className = "tech__drawer-img";
      const img = document.createElement("img");
      img.alt = "";
      img.loading = "eager";
      img.decoding = "sync";
      img.src = imgSrc;
      imgWrap.appendChild(img);
      inner.appendChild(imgWrap);

      panel.appendChild(inner);
      item.appendChild(panel);
      acc.appendChild(item);

      // Accordion behavior
      const setOpen = (open) => {
        item.classList.toggle("is-open", open);
        head.setAttribute("aria-expanded", String(open));
        panel.style.maxHeight = open ? `${inner.scrollHeight}px` : "0px";
      };

      head.addEventListener("click", () => {
        const willOpen = !item.classList.contains("is-open");
        acc.querySelectorAll(".tech__drawer-item").forEach((it) => {
          it.classList.remove("is-open");
          const p = it.querySelector(".tech__drawer-panel");
          if (p) {
            p.style.maxHeight = "0px";
          }
        });
        setOpen(willOpen);
      });

      // Keep a reference to the initial item to open w/o animation
      if (idx === openIndex) {
        firstItem = item;
        firstPanel = panel;
        firstInner = inner;
        firstHead = head;
      }

      // Normal init (collapsed); we'll instant-open the target below
      setOpen(false);

      img.addEventListener(
        "load",
        () => {
          if (item.classList.contains("is-open")) {
            setOpen(true);
          }
        },
        { once: true }
      );
    });

    body.appendChild(acc);

    // Open the targeted item instantly (no height transition)
    if (firstItem && firstPanel && firstInner && firstHead) {
      // Temporarily disable transition on the panel, open immediately,
      // then restore transition after a frame so subsequent toggles animate.
      const prev = firstPanel.style.transition;
      firstPanel.style.transition = "none";

      firstItem.classList.add("is-open");
      firstHead.setAttribute("aria-expanded", "true");
      firstPanel.style.maxHeight = `${firstInner.scrollHeight}px`;

      // Force reflow, then restore transition on next frame
      void firstPanel.offsetHeight;
      requestAnimationFrame(() => {
        firstPanel.style.transition = prev || "";
      });
    }
  }

  section.addEventListener("click", (e) => {
    const card = e.target.closest("[data-tech__card]");
    if (!card || !section.contains(card)) return;
    const cardsWrap = card.closest(".tech__cards");
    const cards = cardsWrap
      ? Array.from(cardsWrap.querySelectorAll("[data-tech__card]"))
      : [];
    const openIdx = Math.max(0, cards.indexOf(card));
    buildDrawerFromCards(cards, openIdx);
    openDrawer();
  });

  const banner = section.querySelector(".tech__banner");
  if (banner) {
    banner.setAttribute("role", "button");
    banner.setAttribute("tabindex", "0");
    banner.setAttribute("aria-label", "Open technology details");

    const openFromBanner = (evt) => {
      evt.preventDefault();
      // Prefer the full mobile set (1..6); fallback to desktop if needed
      const mobileCards = section.querySelectorAll(
        ".tech__cards--mobile [data-tech__card]"
      );
      const desktopCards = section.querySelectorAll(
        ".tech__cards--desk [data-tech__card]"
      );
      const cards = mobileCards.length ? mobileCards : desktopCards;
      if (cards.length) {
        buildDrawerFromCards(Array.from(cards), 0);
        openDrawer();
      }
    };

    banner.addEventListener("click", openFromBanner);
    banner.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") openFromBanner(e);
    });
  }
}

document.addEventListener("DOMContentLoaded", initTech);
// #endregion Technology

// #region Use Cases
(function () {
  function setPanelImageActive(section, nextIndex) {
    const banks = section.querySelectorAll("[data-accordion-images]");
    banks.forEach((wrap) => {
      const imgs = wrap.querySelectorAll(".use-cases__accordion__image");
      imgs.forEach((im) => im.classList.remove("is-active"));
      const next = wrap.querySelector(`[data-accordion-image="${nextIndex}"]`);
      if (next) {
        next.classList.add("is-active");
      }
    });
  }

  function initAccordionSection(section) {
    if (!section) {
      return;
    }
    if (section.dataset.ucBound === "1") {
      return; // prevent double-binding on PJAX
    }
    section.dataset.ucBound = "1";

    const items = Array.from(section.querySelectorAll("[data-accordion-item]"));
    if (!items.length) {
      return;
    }

    const css = getComputedStyle(section);
    const intervalMs = readTimeVar(css, "--accordion-interval", 15000);
    const initialDelayMs = readTimeVar(css, "--accordion-initial-delay", 600);
    const durMs = readTimeVar(css, "--accordion-duration", 1000);

    let openIndex = 0;
    let openingIndex = null;
    let timer = null;
    let pausedByHover = false;
    let inView = true;
    let animToken = 0;
    let pendingAnimEl = null;
    let pendingOnAnimEnd = null;

    const bodyOf = (i) => {
      if (i == null) {
        return null;
      }
      return items[i]?.querySelector("[data-accordion-body]") || null;
    };

    // Freeze a panel to its *current* visual height so we can animate smoothly
    const freezeToCurrent = (el) => {
      if (!el) {
        return 0;
      }
      const h = currentHeight(el);
      el.style.maxHeight = h + "px";
      return h;
    };

    const scheduleNext = () => {
      clearTimeout(timer);
      if (!inView || pausedByHover) {
        return;
      }

      if (pendingAnimEl && pendingOnAnimEnd) {
        pendingAnimEl.removeEventListener("animationend", pendingOnAnimEnd);
        pendingAnimEl = null;
        pendingOnAnimEnd = null;
      }

      const activeItem = items[openIndex] || items[0];
      if (!activeItem) {
        return;
      }

      const cssItem = getComputedStyle(activeItem);
      let progress = parseFloat(cssItem.getPropertyValue("--timer-progress"));
      if (isNaN(progress)) {
        progress = 0;
      }
      const remaining = Math.max(0, Math.round(intervalMs * (1 - progress)));

      const onAnimEnd = (e) => {
        if (e.animationName !== "who-acc-progress") {
          return;
        }
        if (pendingAnimEl) {
          pendingAnimEl.removeEventListener("animationend", onAnimEnd);
          pendingAnimEl = null;
          pendingOnAnimEnd = null;
        }
        setActive(openIndex + 1);
      };

      activeItem.addEventListener("animationend", onAnimEnd, { once: true });
      pendingAnimEl = activeItem;
      pendingOnAnimEnd = onAnimEnd;

      timer = setTimeout(() => {
        if (pendingAnimEl && pendingOnAnimEnd) {
          pendingAnimEl.removeEventListener("animationend", pendingOnAnimEnd);
          pendingAnimEl = null;
          pendingOnAnimEnd = null;
        }
        setActive(openIndex + 1);
      }, remaining + 60);
    };

    // Replace only the setActive function inside your Use Cases region
    const setActive = (index, userInitiated = false, durFactor = 1) => {
      const next = (index + items.length) % items.length;
      if (next === openIndex && openingIndex == null) return;

      const token = ++animToken;

      const prev = openIndex;
      const cancelled = openingIndex;
      if (cancelled === next) return;

      const prevBody = bodyOf(prev);
      const cancBody = bodyOf(cancelled);
      const nextBody = bodyOf(next);
      if (!nextBody) {
        openIndex = next;
        openingIndex = null;
        scheduleNext();
        return;
      }

      const runDur = Math.max(0, Math.round(durMs * (durFactor || 1)));

      // Keep image fade exactly in sync with body animation
      try {
        section.style.setProperty("--accordion-xfade-duration", `${runDur}ms`);
      } catch {}

      // Toggle active state for classes (title/icon styles)
      items.forEach((it, i) => it.classList.toggle("is-active", i === next));

      clearTimeout(timer);
      setPanelImageActive(section, next);

      // --- michu2k-like choreography with lock & strong flush ---

      // 1) Measure heights FIRST (next at its natural size)
      const nextH = nextBody.scrollHeight;
      const prevH = prevBody ? currentHeight(prevBody) : 0;
      const cancH = cancBody ? currentHeight(cancBody) : 0;

      // Get all inner elements
      const prevInner =
        prev != null
          ? items[prev]?.querySelector(
              ".who__accordion-body__inner, .use-cases__accordion-body__inner, [class$='__accordion-body__inner']"
            )
          : null;
      const cancInner =
        cancelled != null
          ? items[cancelled]?.querySelector(
              ".who__accordion-body__inner, .use-cases__accordion-body__inner, [class$='__accordion-body__inner']"
            )
          : null;
      const nextInner = items[next]?.querySelector(
        ".who__accordion-body__inner, .use-cases__accordion-body__inner, [class$='__accordion-body__inner']"
      );

      // 2) Disable transitions temporarily to set start states without triggering animations
      if (prevBody) prevBody.style.transition = "none";
      if (cancBody) cancBody.style.transition = "none";
      nextBody.style.transition = "none";
      if (prevInner) prevInner.style.transition = "none";
      if (cancInner) cancInner.style.transition = "none";
      if (nextInner) nextInner.style.transition = "none";

      // 3) Freeze current bodies to their start heights
      if (prevBody) prevBody.style.maxHeight = prevH + "px";
      if (cancBody) cancBody.style.maxHeight = cancH + "px";

      // 4) Pre-collapse the next body to 0 so it animates 0 -> nextH
      nextBody.style.maxHeight = "0px";

      // 5) Force layout flush to apply the start states
      // eslint-disable-next-line no-unused-expressions
      prevBody && prevBody.offsetHeight;
      // eslint-disable-next-line no-unused-expressions
      cancBody && cancBody.offsetHeight;
      // eslint-disable-next-line no-unused-expressions
      nextBody && nextBody.offsetHeight;

      // 6) Re-enable transitions with consistent durations
      if (prevBody) {
        prevBody.style.transition = "";
        prevBody.style.transitionDuration = runDur + "ms";
      }
      if (cancBody) {
        cancBody.style.transition = "";
        cancBody.style.transitionDuration = runDur + "ms";
      }
      nextBody.style.transition = "";
      nextBody.style.transitionDuration = runDur + "ms";

      if (prevInner) {
        prevInner.style.transition = "";
        prevInner.style.transitionDuration = runDur + "ms";
      }
      if (cancInner) {
        cancInner.style.transition = "";
        cancInner.style.transitionDuration = runDur + "ms";
      }
      if (nextInner) {
        nextInner.style.transition = "";
        nextInner.style.transitionDuration = runDur + "ms";
      }

      // 7) Lock wrapper height temporarily to avoid "breathing"
      const accWrap =
        section.querySelector("[data-accordion]") ||
        section.querySelector(".who__accordion") ||
        section.querySelector(".use-cases__accordion");
      const lockH = Math.max(nextH, prevH, cancH);
      if (accWrap && lockH > 0) accWrap.style.minHeight = lockH + "px";

      // 8) Force another layout flush before triggering the animation
      // eslint-disable-next-line no-unused-expressions
      prevBody && prevBody.offsetHeight;
      // eslint-disable-next-line no-unused-expressions
      cancBody && cancBody.offsetHeight;
      // eslint-disable-next-line no-unused-expressions
      nextBody && nextBody.offsetHeight;
      // eslint-disable-next-line no-unused-expressions
      accWrap && accWrap.offsetHeight;

      // 9) Next frame: flip to end states simultaneously
      requestAnimationFrame(() => {
        if (prevBody) prevBody.style.maxHeight = "0px";
        if (cancBody) cancBody.style.maxHeight = "0px";
        nextBody.style.maxHeight = nextH + "px";
      });

      openingIndex = next;

      const onDone = (e) => {
        if (e.propertyName !== "max-height" || token !== animToken) return;

        // Cleanup inline styles
        if (prevBody) {
          prevBody.style.removeProperty("max-height");
          prevBody.style.removeProperty("transition-duration");
        }
        if (cancBody) {
          cancBody.style.removeProperty("max-height");
          cancBody.style.removeProperty("transition-duration");
        }
        // Keep the open panel's max-height (don't remove it)
        // nextBody already has the correct max-height from the animation
        nextBody.style.removeProperty("transition-duration");

        if (prevInner) prevInner.style.removeProperty("transition-duration");
        if (cancInner) cancInner.style.removeProperty("transition-duration");
        if (nextInner) nextInner.style.removeProperty("transition-duration");

        if (accWrap) accWrap.style.removeProperty("min-height");

        openIndex = next;
        openingIndex = null;

        const inner =
          items[next].querySelector(".who__accordion-body__inner") ||
          items[next].querySelector(".use-cases__accordion-body__inner") ||
          items[next].querySelector("[class$='__accordion-body__inner']");
        if (inner) {
          inner.classList.remove("is-just-opened");
          void inner.offsetWidth;
          inner.classList.add("is-just-opened");
        }

        scheduleNext();
        nextBody.removeEventListener("transitionend", onDone);
      };

      nextBody.addEventListener("transitionend", onDone, { once: true });

      // Safety timeout
      setTimeout(() => {
        if (token === animToken) onDone({ propertyName: "max-height" });
      }, runDur + 120);
    };

    // Click to open (interruptible)
    items.forEach((it, i) => {
      const btn = it.querySelector("[data-accordion-toggle]");
      if (!btn) {
        return;
      }
      btn.addEventListener("click", () => {
        if (openIndex < 0) {
          openIndex = 0;
        }
        setActive(i, true);
      });
    });

    // Pause on hover
    const acc =
      section.querySelector("[data-accordion]") ||
      section.querySelector(".who__accordion") ||
      section.querySelector(".use-cases__accordion");

    if (acc) {
      acc.addEventListener("mouseenter", () => {
        pausedByHover = true;
        clearTimeout(timer);
        if (pendingAnimEl && pendingOnAnimEnd) {
          pendingAnimEl.removeEventListener("animationend", pendingOnAnimEnd);
          pendingAnimEl = null;
          pendingOnAnimEnd = null;
        }
      });

      acc.addEventListener("mouseleave", () => {
        pausedByHover = false;
        scheduleNext();
      });
    }

    // Pause when section not in view
    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            inView = e.isIntersecting;
            if (inView) {
              scheduleNext();
            } else {
              clearTimeout(timer);
              if (pendingAnimEl && pendingOnAnimEnd) {
                pendingAnimEl.removeEventListener(
                  "animationend",
                  pendingOnAnimEnd
                );
                pendingAnimEl = null;
                pendingOnAnimEnd = null;
              }
            }
          });
        },
        { threshold: 0.2 }
      );
      io.observe(section);
    }

    // Initial render: honor any pre-set active item, otherwise open 0
    const firstActive =
      section.querySelector("[data-accordion-item].is-active") || null;
    const initialIdx =
      firstActive?.getAttribute("data-accordion-index") != null
        ? parseInt(firstActive.getAttribute("data-accordion-index"), 10) || 0
        : 0;

    setPanelImageActive(section, initialIdx);

    setTimeout(() => {
      openIndex = -1; // Allow same-index open to run
      setActive(initialIdx, false, 2); // Gentle first open
    }, initialDelayMs);
  }

  // Boot on load + after PJAX swaps
  function bootUseCases() {
    document.querySelectorAll("[data-use-cases]").forEach((sec) => {
      initAccordionSection(sec);
    });
  }

  document.addEventListener("DOMContentLoaded", bootUseCases);
  document.addEventListener("pjax:after-swap", bootUseCases);
})();
// #endregion Use Cases

// #region Commitment Accordion
(() => {
  window.__commitmentCleanups ||= [];

  const currentHeight = (el) => {
    if (!el) return 0;
    const cs = getComputedStyle(el);
    const mh = cs.maxHeight;
    if (!mh || mh === "none" || mh === "0px") return 0;
    const rect = el.getBoundingClientRect();
    return rect.height;
  };

  function initCommitmentAccordion(section) {
    if (!section) return;
    if (section.dataset.commitmentBound === "1") return;
    section.dataset.commitmentBound = "1";

    const items = Array.from(section.querySelectorAll("[data-accordion-item]"));
    if (!items.length) return;

    const css = getComputedStyle(section);
    const durMs = readTimeVar(css, "--accordion-duration", 600);
    let animToken = 0;

    const toggleItem = (index) => {
      const item = items[index];
      if (!item) return;

      const contentEl = item.querySelector("[data-accordion-content]");
      if (!contentEl) return;

      const isActive = item.classList.contains("is-active");
      const token = ++animToken;

      // Toggle active state
      item.classList.toggle("is-active");

      // Update aria-expanded
      const btn = item.querySelector("[data-accordion-button]");
      if (btn) {
        btn.setAttribute("aria-expanded", !isActive ? "true" : "false");
      }

      if (isActive) {
        // Close the item
        const currentH = currentHeight(contentEl);

        // Disable transition temporarily
        contentEl.style.transition = "none";
        contentEl.style.maxHeight = currentH + "px";
        void contentEl.offsetHeight; // Force reflow

        // Re-enable transition
        contentEl.style.transition = "";
        contentEl.style.transitionDuration = durMs + "ms";

        // Force another reflow
        void contentEl.offsetHeight;

        // Animate to closed
        requestAnimationFrame(() => {
          contentEl.style.maxHeight = "0px";
        });

        const onDone = (e) => {
          if (e.propertyName !== "max-height" || token !== animToken) return;
          contentEl.style.removeProperty("max-height");
          contentEl.style.removeProperty("transition-duration");
          contentEl.removeEventListener("transitionend", onDone);
        };

        contentEl.addEventListener("transitionend", onDone, { once: true });

        // Safety timeout
        setTimeout(() => {
          if (token === animToken) onDone({ propertyName: "max-height" });
        }, durMs + 120);
      } else {
        // Open the item
        const targetH = contentEl.scrollHeight;

        // Disable transition temporarily
        contentEl.style.transition = "none";
        contentEl.style.maxHeight = "0px";
        void contentEl.offsetHeight; // Force reflow

        // Re-enable transition
        contentEl.style.transition = "";
        contentEl.style.transitionDuration = durMs + "ms";

        // Force another reflow
        void contentEl.offsetHeight;

        // Animate to open
        requestAnimationFrame(() => {
          contentEl.style.maxHeight = targetH + "px";
        });

        const onDone = (e) => {
          if (e.propertyName !== "max-height" || token !== animToken) return;
          contentEl.style.removeProperty("transition-duration");
          contentEl.removeEventListener("transitionend", onDone);
        };

        contentEl.addEventListener("transitionend", onDone, { once: true });

        // Safety timeout
        setTimeout(() => {
          if (token === animToken) onDone({ propertyName: "max-height" });
        }, durMs + 120);
      }
    };

    // Attach click handlers
    items.forEach((item, i) => {
      const btn = item.querySelector("[data-accordion-button]");
      if (!btn) return;

      const handler = () => toggleItem(i);
      btn.addEventListener("click", handler);

      // Store cleanup
      window.__commitmentCleanups.push(() => {
        btn.removeEventListener("click", handler);
      });
    });
  }

  function runCleanups() {
    window.__commitmentCleanups.forEach((fn) => {
      if (typeof fn === "function") fn();
    });
    window.__commitmentCleanups = [];
  }

  function init() {
    runCleanups();
    const section = document.querySelector("[data-commitment]");
    if (section) {
      initCommitmentAccordion(section);
    }
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("pjax:after-swap", init);
})();
// #endregion Commitment Accordion

// #region Video Hero
(() => {
  // Clean re-init on PJAX
  window.__videoHeroCleanups ||= [];
  window.__videoHeroCleanups.forEach((fn) => {
    try {
      fn();
    } catch {}
  });
  window.__videoHeroCleanups = [];

  // Wait for overlay to finish (used only for fallback IO)
  const waitForTransitionDone = (cb) => {
    if (!document.body.classList.contains("pt-active")) return cb();
    const mo = new MutationObserver(() => {
      if (!document.body.classList.contains("pt-active")) {
        try {
          mo.disconnect();
        } catch {}
        cb();
      }
    });
    mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });
  };

  function initSection(section, { early = false } = {}) {
    const wrap = section.querySelector("[data-video-wrap]");
    const video = section.querySelector("video");
    const btn = section.querySelector(".video-hero__play-button");
    if (!wrap || !video) return;

    // Find the reveal trigger in this section
    const trigger =
      section.querySelector(
        '[data-reveal-group="video-hero"][data-reveal-trigger]'
      ) || section.querySelector(".fx-reveal-up");

    let fired = false;
    const arm = () => {
      if (fired) return;
      fired = true;
      wrap.classList.add("loaded");
    };

    // (A) Title-line reveal → video animation (attach ASAP)
    // If we're in the 'early' phase (pjax:after-swap synchronous), register NOW
    // so we never miss the fx-in mutation from initRevealUp().
    let lineObserver = null;
    if (trigger) {
      if (trigger.classList.contains("fx-in")) {
        // If already revealed by the time we attach, fire immediately
        arm();
      } else {
        lineObserver = new MutationObserver((muts) => {
          for (const m of muts) {
            if (
              m.type === "attributes" &&
              trigger.classList.contains("fx-in")
            ) {
              arm();
              try {
                lineObserver.disconnect();
              } catch {}
              break;
            }
          }
        });
        lineObserver.observe(trigger, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }
    }

    // (B) Fallback IO (attach after overlay to avoid false positives)
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e && (e.isIntersecting || e.intersectionRatio > 0)) {
          arm();
          io.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" }
    );

    // Only delay the fallback, not the mutation observer.
    waitForTransitionDone(() => {
      const r = section.getBoundingClientRect();
      const inView = r.top < innerHeight * 0.9 && r.bottom > innerHeight * 0.1;
      if (inView) arm();
      else io.observe(section);
    });

    // Fullscreen on play button (unchanged)
    const requestFull = async () => {
      try {
        if (video.paused) await video.play().catch(() => {});
        if (video.requestFullscreen) {
          await video.requestFullscreen();
        } else if (video.webkitEnterFullscreen) {
          video.webkitEnterFullscreen();
        } else if (video.webkitRequestFullscreen) {
          await video.webkitRequestFullscreen();
        }
      } catch {}
    };
    const onClick = (e) => {
      e.preventDefault();
      requestFull();
    };
    btn?.addEventListener("click", onClick);

    // Cleanup
    return () => {
      try {
        io.disconnect();
      } catch {}
      try {
        lineObserver && lineObserver.disconnect();
      } catch {}
      btn?.removeEventListener("click", onClick);
    };
  }

  function bootEarly() {
    // Attach ONLY the mutation part immediately so we catch fx-in in sync
    document.querySelectorAll("[data-video-hero]").forEach((sec) => {
      const off = initSection(sec, { early: true });
      if (typeof off === "function") window.__videoHeroCleanups.push(off);
    });
  }

  function boot() {
    // No-op: initSection already set up both mutation + scheduled fallback.
    // Kept for symmetry or future per-page hooks if desired.
  }

  // Initial load
  document.addEventListener("DOMContentLoaded", () => {
    bootEarly(); // Set mutation observers right away
    boot(); // Schedule fallbacks
  });

  // PJAX: attach observers immediately on swap (before initRevealUp runs in reinit)
  document.addEventListener("pjax:after-swap", () => {
    bootEarly(); // Immediate mutation observers (no waiting)
    boot(); // Fallback IO will wait for transition via waitForTransitionDone
  });
})();
// #endregion Video Hero

// #region Who We Are
(() => {
  // PJAX cleanup registry
  window.__whoCarouselCleanups ||= [];
  const runCleanups = () => {
    window.__whoCarouselCleanups.forEach((fn) => {
      try {
        fn();
      } catch {}
    });
    window.__whoCarouselCleanups = [];
  };

  function attachCarousel(section) {
    if (!section) return;

  const container = section.querySelector(".who__carousel-container");
  const track = section.querySelector(".who__carousel");
  if (!container || !track) return;

  // Get --carousel-speed from CSS (default 120s)
  const css = getComputedStyle(section);
  const speedMs = readTimeVar(css, "--carousel-speed", 120000);

  let halfWidth = 0;
  let scrollPosition = 0;
  let isAutoScrolling = true;
  let rafId = null;
  let userInteractionTimeout = null;
  let lastUserScrollTime = 0;

  // Calculate half width (one full set of images)
  const updateDimensions = () => {
    halfWidth = Math.round(track.scrollWidth / 2);
  };

  // Wait for images to load before calculating dimensions
  const imgs = track.querySelectorAll("img");
  let pending = imgs.length;
  if (pending === 0) {
    updateDimensions();
  } else {
    imgs.forEach((img) => {
      if (img.complete) {
        pending--;
        if (pending === 0) updateDimensions();
      } else {
        const onLoad = () => {
          pending--;
          if (pending === 0) updateDimensions();
        };
        img.addEventListener("load", onLoad, { once: true });
        img.addEventListener("error", onLoad, { once: true });
      }
    });
  }

  // Auto-scroll animation
  let lastTime = performance.now();

  const autoScroll = (currentTime) => {
    if (!isAutoScrolling) return;
    if (halfWidth === 0) {
      rafId = requestAnimationFrame(autoScroll);
      return;
    }

    const deltaTime = currentTime - lastTime;
    lastTime = currentTime;

    const pixelsPerMs = halfWidth / speedMs;
    scrollPosition += pixelsPerMs * deltaTime;

    // Seamlessly loop when we reach the halfway point
    if (scrollPosition >= halfWidth) {
      scrollPosition -= halfWidth;
    }

    container.scrollLeft = scrollPosition;
    rafId = requestAnimationFrame(autoScroll);
  };

  // Start auto-scrolling
  const startAutoScroll = () => {
    if (isAutoScrolling) return;
    isAutoScrolling = true;
    lastTime = performance.now();
    rafId = requestAnimationFrame(autoScroll);
  };

  // Stop auto-scrolling
  const stopAutoScroll = () => {
    isAutoScrolling = false;
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  // Handle user scroll
  const onUserScroll = () => {
    lastUserScrollTime = performance.now();

    if (isAutoScrolling) {
      stopAutoScroll();
    }

    // Update our internal scroll position to match user's position
    scrollPosition = container.scrollLeft;

    // Clear existing timeout
    clearTimeout(userInteractionTimeout);

    // Resume auto-scroll after 2 seconds of no interaction
    userInteractionTimeout = setTimeout(() => {
      startAutoScroll();
    }, 2000);
  };

  // Handle manual scroll looping
  const onScroll = () => {
    const currentScroll = container.scrollLeft;

    // If user scrolled (not our auto-scroll)
    if (Math.abs(currentScroll - scrollPosition) > 1) {
      onUserScroll();
    }

    // Handle infinite loop for manual scrolling too
    if (currentScroll >= halfWidth) {
      container.scrollLeft = currentScroll - halfWidth;
      scrollPosition = container.scrollLeft;
    } else if (currentScroll < 0) {
      container.scrollLeft = currentScroll + halfWidth;
      scrollPosition = container.scrollLeft;
    }
  };

  container.addEventListener("scroll", onScroll, { passive: true });

  // Handle resize
  const ro = new ResizeObserver(() => {
    updateDimensions();
    // Restart animation with new dimensions
    if (isAutoScrolling) {
      stopAutoScroll();
      startAutoScroll();
    }
  });
  ro.observe(track);

  // Start the carousel
  updateDimensions();
  rafId = requestAnimationFrame(autoScroll);

  // Cleanup (for PJAX)
  return () => {
    stopAutoScroll();
    clearTimeout(userInteractionTimeout);
    container.removeEventListener("scroll", onScroll);
    ro.disconnect();
  };
}

function initWhoCarousel() {
  runCleanups();
  const section = document.querySelector("[data-who]");
  if (section) {
    const cleanup = attachCarousel(section);
    if (typeof cleanup === "function") {
      window.__whoCarouselCleanups.push(cleanup);
    }
  }
}

// Expose globally
window.initWhoCarousel = initWhoCarousel;

document.addEventListener("DOMContentLoaded", initWhoCarousel);
document.addEventListener("pjax:after-swap", initWhoCarousel);
})();
// #endregion Who We Are — Carousel

// #region Bootstrapping (initial load only)
document.addEventListener("DOMContentLoaded", () => {
  try {
    const who = document.querySelector("[data-who]");
    const useCases = document.querySelector("[data-use-cases]");
    if (
      who &&
      typeof seedAccordionImages === "function" &&
      typeof initAccordionSection === "function"
    ) {
      seedAccordionImages(who);
      initAccordionSection(who);
    }
    if (
      useCases &&
      typeof seedAccordionImages === "function" &&
      typeof initAccordionSection === "function"
    ) {
      seedAccordionImages(useCases);
      initAccordionSection(useCases);
    }
    if (typeof initWhoCarousel === "function") initWhoCarousel();
  } catch {}
});
// #endregion Bootstrapping
